/**
 * Service registry. Each module follows the SAC factory pattern:
 * import the factory, call it with ``baseParams``, export the
 * resulting service object. Consumers import the singleton service
 * directly — no per-call configuration needed.
 */

import ApiKeysEndpoint from "./api-keys/api-keys";
import AssetsEndpoint from "./assets/assets";
import AuthEndpoint from "./auth/auth";
import ComplianceEndpoint from "./compliance/compliance";
import CorsEndpoint from "./cors/cors";
import DashboardEndpoint from "./dashboard/dashboard";
import DataProtectionEndpoint from "./data-protection/data-protection";
import EvalEndpoint from "./eval/eval";
import ObservabilityEndpoint from "./observability/observability";
import PoliciesEndpoint from "./policies/policies";
import ProvidersEndpoint from "./providers/providers";
import RiskEndpoint from "./risk/risk";
import SignupKeysEndpoint from "./signup-keys/signup-keys";
import TenantsEndpoint from "./tenants/tenants";
import UsersEndpoint from "./users/users";
import WorkspacesEndpoint from "./workspaces/workspaces";
import { baseParams } from "./client";

export const ApiKeysService = ApiKeysEndpoint(baseParams);
export const AssetsService = AssetsEndpoint(baseParams);
export const AuthService = AuthEndpoint(baseParams);
export const ComplianceService = ComplianceEndpoint(baseParams);
export const CorsService = CorsEndpoint(baseParams);
export const DashboardService = DashboardEndpoint(baseParams);
export const DataProtectionService = DataProtectionEndpoint(baseParams);
export const EvalService = EvalEndpoint(baseParams);
export const ObservabilityService = ObservabilityEndpoint(baseParams);
export const PoliciesService = PoliciesEndpoint(baseParams);
export const ProvidersService = ProvidersEndpoint(baseParams);
export const RiskService = RiskEndpoint(baseParams);
export const SignupKeysService = SignupKeysEndpoint(baseParams);
export const TenantsService = TenantsEndpoint(baseParams);
export const UsersService = UsersEndpoint(baseParams);
export const WorkspacesService = WorkspacesEndpoint(baseParams);

export { ApiError, refreshAccessToken } from "./client";

export type {
  ApiKeyDetail,
  ApiKeyListFilters,
  ApiKeyStatus,
  ApiKeyStatusFilter,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  DisableApiKeyRequest,
  RevokeApiKeyRequest,
  RevokeApiKeyResponse,
  UpdateApiKeyRequest,
} from "./api-keys/types";

export type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  RefreshResponse,
  RegisterRequest,
  RegisterResponse,
  ValidateSignupKeyResponse,
  SwitchWorkspaceRequest,
} from "./auth/types";

export type {
  ActivityItem,
  ActivitySeverity,
  DashboardMetrics,
} from "./dashboard/types";

export type {
  AuditLogFilters,
  AuditLogOut,
  AuditLogPage,
  AuditStatus,
  CostBreakdownByModel,
  CostBreakdownByWorkspace,
  CostResponse,
  LatencyByStageResponse,
  LatencyPercentiles,
  ObservabilityWindow,
  OverallPosture,
  PolicyEffectBreakdown,
  PolicyEnforcementResponse,
  PostureSummaryResponse,
  TopConcern,
  TopModelEntry,
  TopModelsResponse,
  TopTriggeredPolicy,
  UsageCounters,
  UsdIdrRateGetResponse,
  UsdIdrRateResponse,
  UsdIdrRateUpdatePayload,
} from "./observability/types";

export type {
  AssetBaseResponse,
  AssetChangeListResponse,
  AssetChangeResponse,
  AssetCreatePayload,
  AssetDetailResponse,
  AssetListFilters,
  AssetListResponse,
  AssetTransitionPayload,
  AssetType,
  AssetUpdatePayload,
  DependencyResponse,
  LifecycleState,
  RiskClassification,
} from "./assets/types";

export type {
  CreatePolicyRequest,
  CreateVersionRequest,
  DryRunRequest,
  DryRunResponse,
  PolicyCondition,
  PolicyConditionGroup,
  PolicyConditionOperator,
  PolicyDecisionFilters,
  PolicyDecisionListResponse,
  PolicyDecisionResponse,
  PolicyEffect,
  PolicyEffectConfig,
  PolicyFrameworkClause,
  PolicyListFilters,
  PolicyRuleDefinition,
  PolicyRuleListResponse,
  PolicyRuleResponse,
  PolicyTemplateResponse,
  PolicyVersionResponse,
} from "./policies/types";

export type {
  CategoryCoverage,
  EvidenceFilters,
  EvidenceListResponse,
  EvidenceReferenceResponse,
  EvidenceType,
  FrameworkCode,
  GapClauseSummary,
  Iso42001MaturityResponse,
  ManualEvidenceRequest,
  MaturityLevelSummary,
  MultiFrameworkReadinessResponse,
  NistMaturityResponse,
  ReadinessResponse,
  SubscriptionCreateBody,
  SubscriptionListResponse,
  SubscriptionResponse,
} from "./compliance/types";

export type {
  AllowedOriginAddPayload,
  AllowedOriginConflictDetail,
  AllowedOriginsResponse,
} from "./cors/types";

export type {
  CreateTenantDefaultPolicyPayload,
  InjectionAction,
  LegacyPolicyOut,
  PiiAction,
  UpdateDataProtectionPayload,
} from "./data-protection/types";

export type {
  DimensionSummary,
  FrameworkAlignmentListResponse,
  FrameworkAlignmentResponse,
  MethodologyConfigResponse,
  MethodologyConfigUpdate,
  RecomputeRequest,
  RiskDashboardResponse,
  RiskDimension,
  RiskFramework,
  RiskScoreListResponse,
  RiskScoreSnapshotResponse,
  RiskSignalsFilters,
  RiskSnapshotsFilters,
  ScopeType,
  ScoreBand,
  SignalEventListResponse,
  SignalEventResponse,
  SignalSeverity,
} from "./risk/types";

export type {
  AddMemberRequest,
  CreateWorkspaceRequest,
  ListWorkspacesFilters,
  UpdateMemberRoleRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceListResponse,
  WorkspaceMembership,
  WorkspaceMembershipListResponse,
  WorkspaceRole,
  WorkspaceStatus,
} from "./workspaces/types";

export type {
  UserListFilters,
  UserListResponse,
  UserOut,
  UserRole,
  UserStatus,
} from "./users/types";

export type {
  TenantListResponse,
  TenantProvisionPayload,
  TenantProvisionResponse,
  TenantResponse,
  TenantSummary,
  TenantTrialExtendPayload,
  TenantTrialExtendResponse,
  TenantTrialStatus,
  TenantUpdatePayload,
} from "./tenants/types";

export type {
  CreateSignupKeyRequest,
  SignupKeyCreatedResponse,
  SignupKeyListFilters,
  SignupKeyOut,
} from "./signup-keys/types";

export type {
  CreateProviderCredentialRequest,
  CredentialStatus,
  ListProviderCredentialsFilters,
  ProviderCredentialListResponse,
  ProviderCredentialResponse,
  ProviderType,
  UpdateProviderCredentialRequest,
} from "./providers/types";
